import { z } from 'zod'
import { oc } from '@orpc/contract'
import { CHART_RECORD_RESOURCE_TYPES } from '../../chart-records/handlers/chart-records.contract'
import { soapNoteSchema } from '../../visit/soap-note-payload.contract'
import {
  LAB_INTERPRETATION_CODES,
  labCallFields,
  labPointSchema,
  labQuantitySchema,
} from './patient-me-metric-trends.contract'

export { LAB_INTERPRETATION_CODES }

/**
 * Patient-portal projection of the clinician's SOAP note. An explicit ALLOW-LIST of the
 * fields the portal actually renders (`patient-web-v2/src/pages/Health/components/progressNote/`),
 * so a field added to `soapNoteSchema` upstream stays server-side until it is deliberately
 * added here rather than reaching the patient's browser by default.
 *
 * Deliberately withheld: `billing` / `billingSuggestion` / `selectedCoverageId` (CPT codes,
 * prices, modifiers and the AI's coding rationale), `mdm` (complexity justification written
 * for coders and auditors), `implausibleVitals` (internal QA flag), `tags`, `familyHistory`,
 * and the clinician-only coding arrays the portal does not render.
 *
 * Withholding is the default, not the goal: a field the patient is entitled to see belongs on
 * this list. `aiAssisted` is here for exactly that reason — see its note below.
 */
export const patientStructuredNoteSchema = z.object({
  soapNote: soapNoteSchema
    .pick({
      // Encounter + clinician-authored narrative.
      noteDate: true, // also the list endpoint's sort key — see `getSortDate`
      visitType: true,
      examReason: true,
      prob: true,
      objective: true,
      assessment: true,
      plan: true,
      followUp: true,

      // Vitals, with their per-vital notes and unit tokens.
      bpSystolic: true,
      bpDiastolic: true,
      bpNote: true,
      hr: true,
      hrNote: true,
      rr: true,
      rrNote: true,
      temp: true,
      tempNote: true,
      tempUnit: true,
      wt: true,
      wtNote: true,
      wtUnit: true,
      ht: true,
      htNote: true,
      htUnit: true,
      bloodGlucose: true,
      bloodGlucoseNote: true,
      glucoseUnit: true,
      o2: true,
      o2Note: true,
      pain: true,
      painNote: true,
      bmi: true,
      hc: true,
      hcNote: true,

      // Structured clinical lists the portal renders as tables.
      reviewOfSystemsStructured: true,
      allergies: true,
      assessment1: true,
      pastMedicalHistory: true,
      medsReviewed: true,
      treatmentsPrescribed: true,
      testsOrdered: true,

      // Deliberately INCLUDED, unlike the clinician-only fields above: the AI
      // Documentation Disclosure this flag drives is written for the patient, and
      // the v1 portal, the provider console, the note PDF and the C-CDA all show it
      // (PD-1613). Withholding it here is the one way the portal disagrees with
      // every other copy of the same note.
      aiAssisted: true,
    })
    .partial(),
  context: z.object({ practitionerDisplay: z.string().optional() }),
})

/**
 * FHIR data-absent-reason codes a lab may send as the only reason a test has no value. The portal
 * words each one; 'as-text' is left out, since with no text it says nothing.
 */
export const LAB_DATA_ABSENT_REASON_CODES = [
  'unknown',
  'asked-unknown',
  'temp-unknown',
  'not-asked',
  'asked-declined',
  'masked',
  'not-applicable',
  'unsupported',
  'error',
  'not-a-number',
  'negative-infinity',
  'positive-infinity',
  'not-performed',
  'not-permitted',
] as const

// A value the portal words, on a test or on one of its components: a yes/no, a date, a time, a
// span, a range or a ratio.
const labWordedValueSchemas = [
  z.object({ kind: z.literal('boolean'), value: z.boolean() }),
  z.object({ kind: z.literal('dateTime'), value: z.string() }),
  z.object({ kind: z.literal('time'), value: z.string() }),
  z.object({ kind: z.literal('period'), start: z.string().nullable(), end: z.string().nullable() }),
  z.object({
    kind: z.literal('range'),
    low: labQuantitySchema.nullable(),
    high: labQuantitySchema.nullable(),
  }),
  z.object({
    kind: z.literal('ratio'),
    numerator: labQuantitySchema,
    denominator: labQuantitySchema.nullable(),
  }),
] as const

/**
 * A text result's value that the portal words: a worded value (above), named components, a coded
 * reason the test has no value, or that it has no result. Patient copy (Yes/No, local dates, the
 * joins, the reason's wording) lives in the frontend; the lab's own strings, coded answers and
 * worded not-performed reasons stay plain text in `value`.
 */
export const labStructuredValueSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('absent'), code: z.enum(LAB_DATA_ABSENT_REASON_CODES) }),
  // A named test with no result: yet, while it or its report is in progress; or none reported.
  z.object({ kind: z.literal('pending') }),
  z.object({ kind: z.literal('notReported') }),
  ...labWordedValueSchemas,
  z.object({
    kind: z.literal('components'),
    items: z.array(
      z.object({
        name: z.string().nullable(),
        value: z.union([
          z.string(),
          labQuantitySchema,
          z.discriminatedUnion('kind', [...labWordedValueSchemas]),
        ]),
        // The part's own range and the lab's call on it, read as a table row's are; each only
        // when the lab gave one.
        referenceLow: labPointSchema.shape.referenceLow.unwrap().optional(),
        referenceHigh: labPointSchema.shape.referenceHigh.unwrap().optional(),
        referenceText: labPointSchema.shape.referenceText.unwrap().optional(),
        otherUnitRange: labPointSchema.shape.otherUnitRange,
        abnormalFlag: labPointSchema.shape.abnormalFlag.unwrap().optional(),
        ...labCallFields,
      }),
    ),
  }),
])

/**
 * Patient-portal projection of a lab report's parsed results (PD-1724), an ALLOW-LIST like
 * `patientStructuredNoteSchema`. Each numeric result is PD-1601's `labPointSchema` plus the
 * analyte's name and the lab's note on it; each non-numeric member (a lab's interpretation, a
 * performing-site note, a POSITIVE screen) is a text result of name, value, note text and the same
 * abnormal flag. Both are grouped under their panel, and the report header carries only the items
 * the lab view renders.
 *
 * Deliberately withheld: every raw FHIR resource, Observation ids and raw interpretation codings
 * (the derived `abnormalFlag` is what the portal shows, beside the lab's own call: its text or
 * display, else a known HL7 code from LAB_INTERPRETATION_CODES, else an unknown code as sent),
 * performer and specimen references, and identifiers other than the accession and lab reference
 * numbers.
 */
export const patientLabReportSchema = z.object({
  source: z.string().optional(),
  accessionNumber: z.string().optional(),
  issued: z.string().optional(),
  referringDoctor: z.string().optional(),
  lastUpdated: z.string().optional(),
  labReference: z.string().optional(),
  status: z.string().optional(),
  panels: z.array(
    z.object({
      name: z.string().nullable(),
      collected: z.string().optional(),
      received: z.string().optional(),
      reported: z.string().optional(),
      results: z.array(
        labPointSchema.extend({
          name: z.string().nullable(),
          note: z.string().nullable(),
        }),
      ),
      texts: z.array(
        z.object({
          name: z.string().nullable(),
          value: z.string().nullable(),
          structuredValue: labStructuredValueSchema.optional(),
          note: z.string().nullable(),
          abnormalFlag: labPointSchema.shape.abnormalFlag,
          ...labCallFields,
        }),
      ),
    }),
  ),
})

export const PATIENT_HEALTH_RECORD_SECTIONS = [
  'progress-notes',
  'history-physical',
  'visit-summaries',
  'labs-imaging',
  'orders',
  'documents',
] as const

const healthRecordEntrySchema = z.object({
  section: z.enum(PATIENT_HEALTH_RECORD_SECTIONS),
  resourceType: z.enum(CHART_RECORD_RESOURCE_TYPES),
  resourceId: z.string(),
  title: z.string(),
  /** FHIR date/dateTime/instant copied from the resource's display-date field. */
  date: z.string().optional(),
})

const attachmentSchema = z.object({
  contentType: z.string(),
  url: z.string().optional(),
  data: z.string().optional(),
  title: z.string().optional(),
})

/**
 * A signed addendum, returned as part of its parent progress note rather than as a record of
 * its own — an addendum amends a note, it is not a separate document in the patient's record.
 */
const addendumSchema = z.object({
  id: z.string(),
  text: z.string(),
  reason: z.string().optional(),
  date: z.string().optional(),
})

const provenanceSchema = z.object({
  source: z.string().optional(),
  provider: z.string().optional(),
  providers: z.array(z.string()).optional(),
  type: z.string().optional(),
  documentName: z.string().optional(),
  recordedAt: z.string().optional(),
})

export const patientHealthRecordList = oc
  .route({ method: 'GET', path: '/v2/patient/me/health-record' })
  .input(z.object({}))
  .output(
    z.object({
      items: z.array(healthRecordEntrySchema),
      unavailableSections: z.array(z.enum(PATIENT_HEALTH_RECORD_SECTIONS)),
    }),
  )

export const patientHealthRecordGet = oc
  .route({
    method: 'GET',
    path: '/v2/patient/me/health-record/{resourceType}/{resourceId}',
  })
  .input(
    z.object({
      resourceType: z.enum(CHART_RECORD_RESOURCE_TYPES),
      resourceId: z.string().min(1),
    }),
  )
  .output(
    z.object({
      resourceType: z.enum(CHART_RECORD_RESOURCE_TYPES),
      resourceId: z.string(),
      attachments: z.array(attachmentSchema),
      addenda: z.array(addendumSchema).optional(),
      provenance: provenanceSchema.optional(),
      structuredNote: patientStructuredNoteSchema.optional(),
      // Set only for a lab report with at least one parsed result.
      lab: patientLabReportSchema.optional(),
    }),
  )
